import { useState } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import styles from "./SupportPackageModal.module.css";

/**
 * Add / Edit Support Service (Support spec v1.0 §6.4).
 *
 * §6.4 lists the whole form: Service Name, Code, Customer-facing Text, Internal Description,
 * Active. There is deliberately no price and no product type here - §4 says a service "has no
 * fixed price and no fixed product type", because the same service is included in one level and
 * chargeable in another. That decision belongs to the assignment (§5), not to the catalogue.
 */
const emptyForm = { name: "", code: "", customerText: "", internalDescription: "", active: true };

const toForm = (s) =>
  s
    ? {
        name: s.name ?? "",
        code: s.code ?? "",
        customerText: s.customerText ?? "",
        internalDescription: s.internalDescription ?? "",
        active: s.active ?? true,
      }
    : emptyForm;

const SupportServiceModal = ({ service, saving, onClose, onSave }) => {
  const [form, setForm] = useState(() => toForm(service));
  const [errors, setErrors] = useState({});
  const isEdit = !!service;
  // §7: once a service has been used on a booking its code is read-only. The server refuses the
  // change either way; locking the box means the operator finds out before typing, not after.
  const codeLocked = isEdit && !!service.used;

  const set = (k) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    // §12's three required fields. Checked here so the operator sees them beside the box rather
    // than as one toast from the server.
    if (!form.name.trim()) next.name = "Service name is required";
    if (!form.code.trim()) next.code = "Code is required";
    else if (!/^[A-Za-z0-9_-]+$/.test(form.code.trim())) {
      next.code = "Use only letters, numbers, underscore and hyphen";
    }
    if (!form.customerText.trim()) next.customerText = "Customer-facing text is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      name: form.name.trim(),
      customerText: form.customerText.trim(),
      internalDescription: form.internalDescription.trim() || null,
      active: form.active,
    };
    // Leaving the code out of an edit payload is what keeps a locked code untouched.
    if (!codeLocked) payload.code = form.code.trim().toUpperCase();
    onSave(payload);
  };

  return (
    <BaseModal
      open
      onClose={onClose}
      title={isEdit ? "Edit Support Service" : "Add Support Service"}
      width="620px"
      footer={
        <div className={styles.footer}>
          <button type="button" className={styles.cancel} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form="support-service-form" className={styles.primary} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Service"}
          </button>
        </div>
      }
    >
      <form id="support-service-form" className={styles.form} onSubmit={submit}>
        <div className={styles.field}>
          <label>Service Name *</label>
          <input
            value={form.name}
            onChange={set("name")}
            placeholder="e.g. Booking change assistance"
            className={errors.name ? styles.invalid : undefined}
          />
          {errors.name && <span className={styles.err}>{errors.name}</span>}
          <span className={styles.hint}>Internal name, used in the dashboard.</span>
        </div>

        <div className={styles.field}>
          <label>Code *</label>
          <input
            value={form.code}
            onChange={set("code")}
            placeholder="e.g. CHANGE_ASSIST"
            disabled={codeLocked}
            className={errors.code ? styles.invalid : undefined}
          />
          {errors.code && <span className={styles.err}>{errors.code}</span>}
          <span className={styles.hint}>
            {codeLocked
              ? "This service has been used on a booking, so its code can no longer be changed."
              : "Unique technical code. Stored on every booking that uses this service."}
          </span>
        </div>

        <div className={styles.field}>
          <label>Customer-facing Text *</label>
          <input
            value={form.customerText}
            onChange={set("customerText")}
            placeholder="e.g. Booking change assistance"
            className={errors.customerText ? styles.invalid : undefined}
          />
          {errors.customerText && <span className={styles.err}>{errors.customerText}</span>}
          <span className={styles.hint}>The line the traveller reads on the checkout card.</span>
        </div>

        <div className={styles.field}>
          <label>Internal Description</label>
          <textarea
            rows={3}
            value={form.internalDescription}
            onChange={set("internalDescription")}
            placeholder="Backend-only note…"
          />
        </div>

        <div className={styles.toggleRow}>
          <div>
            <label>Active</label>
            <span className={styles.hint}>
              Inactive services cannot be newly assigned. Existing assignments and past bookings
              are not affected.
            </span>
          </div>
          <ToggleSwitch checked={form.active} onChange={() => setForm((f) => ({ ...f, active: !f.active }))} />
        </div>
      </form>
    </BaseModal>
  );
};

export default SupportServiceModal;
